import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { MessageCircle, Camera, Siren, MapPin, Bell, Dog, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { PrimaryButton } from "@/components/kit";
import InstallPWA from "@/components/InstallPWA";

const FEATURES = [
  { icon: MessageCircle, title: "Ask AI anything", body: "Unlimited canine health Q&A powered by Gemini." },
  { icon: Camera, title: "Photo symptom analysis", body: "Upload a photo, get instant preliminary guidance." },
  { icon: Siren, title: "Emergency red-flags", body: "Smart alerts when it's time to see a vet — fast." },
  { icon: MapPin, title: "Find local vets", body: "Nearby clinics with ratings, hours & 24/7 filter." },
  { icon: Bell, title: "Vaccine reminders", body: "Never miss a shot or a vet visit again." },
  { icon: Dog, title: "Multi-dog profiles", body: "Personalized advice for every pup in your home." },
];

export default function Welcome() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/app" + window.location.search, { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-[#FCFBF8] text-[#2D2B2A]">
      {/* Nav */}
      <header className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Logo size={40} to={null} />
        <div className="flex items-center gap-2">
          <InstallPWA
            label="Install app"
            className="hidden sm:inline-flex items-center gap-2 rounded-full border-2 border-[#E8E3DA] text-[#4A4746] font-bold px-4 py-2 hover:border-[#C4BFB8] transition-colors"
          />
          <Link
            to="/login"
            data-testid="welcome-login-link"
            className="px-4 py-2 rounded-full font-bold text-[#4A4746] hover:bg-[#F2EFE9] transition-colors"
          >
            Log in
          </Link>
          <Link to="/signup" data-testid="welcome-signup-btn">
            <PrimaryButton className="!py-2.5">Get started</PrimaryButton>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F6DED5] text-[#B54828] text-xs font-extrabold uppercase tracking-wide">
            <Dog size={14} /> For dog parents
          </span>
          <h1 className="font-display text-5xl sm:text-6xl font-black leading-[1.05] mt-5 tracking-tight">
            Your dog&apos;s wellbeing, in your pocket.
          </h1>
          <p className="text-lg text-[#4A4746] mt-5 max-w-lg leading-relaxed">
            AI-powered canine health guidance, symptom photo analysis, and trusted
            local vets — all in one warm, friendly app.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/signup" data-testid="hero-signup-btn">
              <PrimaryButton className="!px-8 !py-3.5 text-base">Create free account</PrimaryButton>
            </Link>
            <Link
              to="/login"
              className="px-8 py-3.5 rounded-full font-extrabold border-2 border-[#E8E3DA] hover:border-[#C4BFB8] transition-colors"
            >
              I have an account
            </Link>
          </div>
          <div className="flex items-center gap-5 mt-7 text-sm text-[#4A4746]">
            <span className="flex items-center gap-1.5"><Check size={16} color="#6F8F72" /> 3 free AI questions/mo</span>
            <span className="flex items-center gap-1.5"><Check size={16} color="#6F8F72" /> Cancel anytime</span>
          </div>
          <div className="mt-5 flex items-center gap-3 sm:hidden">
            <InstallPWA
              label="Install the app"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#E8E3DA] text-[#4A4746] font-bold px-5 py-2.5 hover:border-[#C4BFB8] transition-colors"
            />
            <span className="text-xs text-[#9A938B]">Runs on Android &amp; iPhone — no app store</span>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-6 bg-[#F6DED5] rounded-[3rem] rotate-3 opacity-60" />
          <img
            src="/wooflect-hero.webp"
            alt="Happy dog in the mountains"
            className="relative w-full max-w-md mx-auto rounded-[2.5rem] shadow-2xl bg-white object-cover aspect-square"
          />
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y border-[#E8E3DA]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
          <h2 className="font-display text-3xl font-extrabold text-center">Everything your pup needs</h2>
          <p className="text-center text-[#4A4746] mt-2">One friendly companion for every wag, worry and woof.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {FEATURES.map((f) => (
              <div key={f.title} className="p-6 rounded-3xl bg-[#FCFBF8] border border-[#E8E3DA] hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-2xl bg-[#F6DED5] flex items-center justify-center mb-4">
                  <f.icon size={24} color="#E86A45" />
                </div>
                <h3 className="font-display font-extrabold text-lg">{f.title}</h3>
                <p className="text-[#4A4746] mt-1.5 text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-black">Ready when your dog needs you.</h2>
        <p className="text-[#4A4746] mt-3 max-w-xl mx-auto">Join Wooflect and get calm, clear guidance in seconds.</p>
        <Link to="/signup" className="inline-block mt-7" data-testid="cta-signup-btn">
          <PrimaryButton className="!px-9 !py-4 text-base">Get started free</PrimaryButton>
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E8E3DA] bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#4A4746]">
          <Logo size={30} to={null} />
          <div className="flex items-center gap-5">
            <Link to="/legal/terms" className="hover:text-[#E86A45]">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-[#E86A45]">Privacy</Link>
            <Link to="/legal/disclaimer" className="hover:text-[#E86A45]">Disclaimer</Link>
            <Link to="/sms-optin" className="hover:text-[#E86A45]">Text alerts</Link>
            <Link to="/for-vets" className="hover:text-[#E86A45]">For vets</Link>
            <Link to="/waitlist" className="hover:text-[#E86A45]">Join beta</Link>
          </div>
          <span>© 2026 Happy Big Dogs, LLC</span>
        </div>
      </footer>
    </div>
  );
}
